import {
  PermissionCode,
  ProductCode,
  type IntranetAuthorGrant,
  type IntranetAuthorGrantListQuery,
  type IntranetAuthorGrantPerson,
} from '@gb-ticket/shared-types';
import { Loader2Icon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { ApiRequestError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { PersonChip } from '@/platform/components/person-chip';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { adminPanelClassName, adminTableFrameClassName } from '@/platform/features/admin-panel';
import {
  useAuthorGrants,
  useCreateAuthorGrant,
  useRemoveAuthorGrant,
} from '@/platform/features/users/use-author-grants';
import { useUsers } from '@/platform/features/users/use-users';

const SORTS = [
  { value: 'createdAt:desc', label: 'Neueste zuerst' },
  { value: 'createdAt:asc', label: 'Älteste zuerst' },
  { value: 'actor:asc', label: 'Ersteller' },
  { value: 'author:asc', label: 'Angezeigter Autor' },
] as const;

function personDetail(person: IntranetAuthorGrantPerson): string | null {
  return person.departmentName ?? person.jobTitle;
}

function PersonCell({ person }: { person: IntranetAuthorGrantPerson }) {
  const detail = personDetail(person);
  return (
    <div className="min-w-0">
      <PersonChip
        displayName={person.displayName}
        firstName={person.firstName}
        lastName={person.lastName}
        profilePhotoUrl={person.profilePhotoUrl}
      />
      {detail ? <p className="truncate pl-9 text-xs text-muted-foreground">{detail}</p> : null}
    </div>
  );
}

export function AuthorGrantsPage() {
  const copy = useVoiceCopy();
  const me = useMe();
  const canManage = hasPermission(me.data, PermissionCode.USERS_MANAGE);
  const intranetOn = me.data?.products?.includes(ProductCode.INTRANET) === true;
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState<string | undefined>();
  const [sort, setSort] = useState<(typeof SORTS)[number]['value']>('createdAt:desc');
  const [assignOpen, setAssignOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<IntranetAuthorGrant | null>(null);
  const [sortBy, sortOrder] = sort.split(':') as [
    IntranetAuthorGrantListQuery['sortBy'],
    IntranetAuthorGrantListQuery['sortOrder'],
  ];
  const grants = useAuthorGrants({ q, sortBy, sortOrder }, { enabled: canManage && intranetOn });
  const remove = useRemoveAuthorGrant();

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = searchInput.trim() || undefined;
      setQ((current) => (current === next ? current : next));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  async function confirmRemove() {
    if (!removeTarget) {
      return;
    }
    try {
      await remove.mutateAsync(removeTarget.id);
      toast.success('Berechtigung entfernt.');
      setRemoveTarget(null);
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Entfernen fehlgeschlagen.');
    }
  }

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Beiträge für andere"
        description={copy('platform.users.author-grants.intro')}
        action={
          canManage && intranetOn ? (
            <Button size="sm" onClick={() => setAssignOpen(true)}>
              <PlusIcon aria-hidden />
              Berechtigung zuweisen
            </Button>
          ) : null
        }
      />

      {!canManage ? (
        <p role="alert" className={adminPanelClassName}>
          {copy('platform.users.author-grants.denied')}
        </p>
      ) : !intranetOn ? (
        <p role="alert" className={adminPanelClassName}>
          Das Intranet ist für diesen Mandanten nicht aktiv.
        </p>
      ) : (
        <>
          <div className={`${adminPanelClassName} flex flex-col gap-3 sm:flex-row sm:items-end`}>
            <div className="min-w-0 flex-1 space-y-1">
              <Label htmlFor="author-grant-search">Suche</Label>
              <Input
                id="author-grant-search"
                value={searchInput}
                placeholder="Name oder Abteilung"
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="author-grant-sort">Sortieren</Label>
              <Select value={sort} onValueChange={(value) => setSort(value as typeof sort)}>
                <SelectTrigger id="author-grant-sort" className="w-full sm:w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORTS.map((entry) => (
                    <SelectItem key={entry.value} value={entry.value}>
                      {entry.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {grants.isPending ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2Icon className="size-4 animate-spin" aria-hidden />
              Berechtigungen werden geladen …
            </p>
          ) : grants.isError ? (
            <p role="alert" className="text-sm text-destructive">
              Die Liste konnte nicht geladen werden.
            </p>
          ) : grants.data.items.length === 0 ? (
            <p className={adminPanelClassName}>{copy('platform.users.author-grants.empty')}</p>
          ) : (
            <div className={adminTableFrameClassName}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ersteller</TableHead>
                    <TableHead>Angezeigter Autor</TableHead>
                    <TableHead>Erstellt am</TableHead>
                    <TableHead className="w-12">
                      <span className="sr-only">Aktionen</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {grants.data.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <PersonCell person={item.actor} />
                      </TableCell>
                      <TableCell>
                        <PersonCell person={item.author} />
                      </TableCell>
                      <TableCell>{formatDate(item.createdAt)}</TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Berechtigung von ${item.actor.displayName} für ${item.author.displayName} entfernen`}
                          onClick={() => setRemoveTarget(item)}
                        >
                          <Trash2Icon aria-hidden />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}

      <AssignAuthorGrantDialog open={assignOpen} onClose={() => setAssignOpen(false)} />

      <AlertDialog
        open={removeTarget != null}
        onOpenChange={(open) => (open ? undefined : setRemoveTarget(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Berechtigung entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget
                ? `${removeTarget.actor.displayName} darf dann nicht mehr im Namen von ${removeTarget.author.displayName} veröffentlichen.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmRemove()}>Entfernen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function AssignAuthorGrantDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateAuthorGrant();
  const [actorUserId, setActorUserId] = useState('');
  const [authorUserId, setAuthorUserId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState<string | undefined>();
  const people = useUsers(
    {
      page: 1,
      pageSize: 200,
      status: 'active',
      sortBy: 'lastName',
      sortOrder: 'asc',
      q,
    },
    { enabled: open },
  );
  const samePerson = actorUserId !== '' && actorUserId === authorUserId;

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = searchInput.trim() || undefined;
      setQ((current) => (current === next ? current : next));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  function close() {
    setActorUserId('');
    setAuthorUserId('');
    setSearchInput('');
    setQ(undefined);
    onClose();
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!actorUserId || !authorUserId || samePerson) {
      return;
    }
    try {
      await create.mutateAsync({ actorUserId, authorUserId });
      toast.success('Berechtigung zugewiesen.');
      close();
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Zuweisen fehlgeschlagen.');
    }
  }

  const options = people.data?.items ?? [];

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Berechtigung zuweisen</DialogTitle>
          <DialogDescription>
            Der Ersteller darf Beiträge im Namen des angezeigten Autors veröffentlichen. Beide
            Personen müssen aktive Benutzer sein.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-3" onSubmit={(event) => void onSubmit(event)}>
          <div className="space-y-1">
            <Label htmlFor="author-grant-person-search">Person suchen</Label>
            <Input
              id="author-grant-person-search"
              value={searchInput}
              placeholder="Name"
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="author-grant-actor">Ersteller</Label>
              <Select value={actorUserId || undefined} onValueChange={setActorUserId}>
                <SelectTrigger id="author-grant-actor" className="w-full">
                  <SelectValue placeholder="Person wählen" />
                </SelectTrigger>
                <SelectContent>
                  {options.map((person) => (
                    <SelectItem key={person.id} value={person.id}>
                      {person.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="author-grant-author">Angezeigter Autor</Label>
              <Select value={authorUserId || undefined} onValueChange={setAuthorUserId}>
                <SelectTrigger id="author-grant-author" className="w-full">
                  <SelectValue placeholder="Person wählen" />
                </SelectTrigger>
                <SelectContent>
                  {options.map((person) => (
                    <SelectItem key={person.id} value={person.id}>
                      {person.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {samePerson ? (
            <p role="alert" className="text-sm text-destructive">
              Ersteller und angezeigter Autor müssen verschieden sein.
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={!actorUserId || !authorUserId || samePerson || create.isPending}
            >
              Berechtigung zuweisen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
